import React, { useMemo, useState, useCallback } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { useIntl } from 'umi';
import { Segment } from 'tea-component';
import RangePicker from '@home/components/RangePicker';
import '@/pages/home/components/RangePicker/index.scss';
import '@intelligence/components/Filter/FilterBase/index.scss';
import useSafeTimerRef from '@intelligence/v3/_hooks/useSafeTimerRef';
import { showTimeByDateType } from '@intelligence/utils/commonUtils';
import { setShortcutOptionFun } from '@intelligence/components/Filter/FilterBase/_config';
import {
  getGranularityByType,
  getRangePickerBoundaryValueByType,
  getRangePickerDefaultComponentValue,
  getValidatedRangePickerValue,
  getRangePickerValue,
  isReleaseRangePickerItem,
  getTypeByGranularity,
  DEFAULT_RANGE_PICKER_TYPE_LIST,
} from './utils';
import { LANGUAGE_CN } from '@home/_config/config';
import { FILTER_TYPE } from '../filterTypes';

import type { IFilterRenderProps, IFilterTypeEntry } from '@intelligence/v3/filterCore/registry';
import type { IFilterControlModule } from '../controlModule';
import type { IRangePickerFilterValue } from './types';

const GRANULARITY_I18N_KEY_MAP: IRecord<string> = {
  date: 'dbi_day',
  week: 'dbi_week',
  month: 'dbi_month',
};

const parseTimeVal = (val: any[]): any[] => {
  if (!Array.isArray(val) || val.length !== 2) {
    return ['', ''];
  }

  return val.map((time: any) => {
    const parsed = moment(time);
    return parsed.isValid() ? parsed : '';
  });
};

const getSafeDateMoment = (value: any): moment.Moment | null => {
  if (moment.isMoment(value)) {
    if (value.isValid()) {
      return value;
    }

    return null;
  }

  if (value instanceof Date) {
    const dateMoment = moment(value);

    if (dateMoment.isValid()) {
      return dateMoment;
    }

    return null;
  }

  if (typeof value === 'string') {
    const dateMoment = moment(value, moment.ISO_8601, true);

    if (dateMoment.isValid()) {
      return dateMoment;
    }

    return null;
  }

  return null;
};

const RangePickerRenderer: React.FC<IFilterRenderProps<IRangePickerFilterValue>> = (props) => {
  const {
    selectClassName,
    customComponentClassName,
    rangePickerClassName,
    overlayClassName,
    showSelectType = true,
    showShortcut = true,
    startOfWeek = 0,
    typeList = DEFAULT_RANGE_PICKER_TYPE_LIST,
    componentDefault,
    sinceReleaseTime = '',
    earliestDataTime = '',
    rangeTimeOtherStyle = false,
    isSingleSelect = false,
    autoRange,
    item,
    field,
    value,
    onChange,
    ...rest
  } = props;
  const { locale, formatMessage } = useIntl();
  const { timerRef: resetTimerRef } = useSafeTimerRef(clearTimeout);
  const [autoResetShortcutByValueChange, setAutoResetShortcutByValueChange] = useState(false);
  const { granularity, startDate, endDate } = getRangePickerValue(value);
  const rawTimeVal: [string, string] = [startDate, endDate];
  const type = getTypeByGranularity(granularity);
  const timeVal = parseTimeVal(rawTimeVal);
  const componentDefaultValues = useMemo(() => {
    return {
      ...getRangePickerDefaultComponentValue(startOfWeek),
      ...(componentDefault || {}),
    };
  }, [componentDefault, startOfWeek]);
  const defaultValueProp = useMemo(() => {
    return {
      date: componentDefaultValues.daily,
      week: componentDefaultValues.weekly,
      month: componentDefaultValues.monthly,
    };
  }, [componentDefaultValues]);
  const isReleaseTime = isReleaseRangePickerItem(item);

  const handleSetShortcutOption = useCallback(
    () => setShortcutOptionFun(type, sinceReleaseTime, earliestDataTime),
    [type, sinceReleaseTime, earliestDataTime],
  );

  const handleTypeChange = (newType: string): void => {
    const newGranularity = getGranularityByType(newType);
    const defaultVal = componentDefaultValues[newGranularity];
    const hasCurrentValue = rawTimeVal.some(Boolean);
    const normalizedCurrentValue = getValidatedRangePickerValue({
      granularity: newGranularity,
      startDate: rawTimeVal[0],
      endDate: rawTimeVal[1],
      granularities: [newGranularity],
      startOfWeek,
    });

    onChange({
      granularity: newGranularity,
      startDate: normalizedCurrentValue?.startDate || (hasCurrentValue ? rawTimeVal[0] : defaultVal?.[0] || ''),
      endDate: normalizedCurrentValue?.endDate || (hasCurrentValue ? rawTimeVal[1] : defaultVal?.[1] || ''),
    });
    setAutoResetShortcutByValueChange(true);
    resetTimerRef.current = setTimeout(() => {
      setAutoResetShortcutByValueChange(false);
    }, 0);
  };

  const handleDateChange = (dateValue: any, dateType: string): void => {
    const newGranularity = getGranularityByType(dateType);
    let finalStart = '';
    let finalEnd = '';

    if (Array.isArray(dateValue) && dateValue.length === 2) {
      const [dateStart, dateEnd] = dateValue;

      if (dateStart === '' && dateEnd === '') {
        const defaultVal = componentDefaultValues[newGranularity];
        finalStart = defaultVal?.[0] || '';
        finalEnd = defaultVal?.[1] || '';
      } else {
        const dateStartMoment = getSafeDateMoment(dateStart);
        const dateEndMoment = getSafeDateMoment(isSingleSelect ? (dateEnd || dateStart) : dateEnd);

        if (!dateStartMoment || !dateEndMoment) {
          return;
        }

        const nextValue = getRangePickerBoundaryValueByType({
          type: dateType,
          startDate: dateStartMoment.format('YYYY-MM-DD'),
          endDate: dateEndMoment.format('YYYY-MM-DD'),
          startOfWeek,
        });

        finalStart = nextValue.startDate;
        finalEnd = isSingleSelect ? nextValue.startDate : nextValue.endDate;
      }
    }

    onChange({
      granularity: newGranularity,
      startDate: finalStart,
      endDate: finalEnd,
    });
  };

  return (
    <div
      className={classnames('search-item', 'search-item-date', {
        'release-time-picker': isReleaseTime,
      })}
      key={field}
    >
      {rangeTimeOtherStyle && typeList.length > 1 && (
        <>
          <Segment
            className="segment-light details-storerank-segment"
            value={type}
            onChange={handleTypeChange}
            options={typeList.map((itemType: string) => ({
              value: itemType,
              text: formatMessage({
                id: GRANULARITY_I18N_KEY_MAP[itemType] || GRANULARITY_I18N_KEY_MAP.month,
              }),
            }))}
          />
          <span className="dbi_white_space">-</span>
        </>
      )}
      <RangePicker
        {...rest}
        className="search-border"
        overlayClassName={classnames(overlayClassName, {
          'dtb-rangepicker-has-firstsince-shotcut': sinceReleaseTime !== '',
        })}
        rangeTimeOtherStyle={rangeTimeOtherStyle}
        selectClassName={selectClassName}
        customComponentClassName={customComponentClassName}
        rangePickerClassName={rangePickerClassName}
        language={locale === LANGUAGE_CN ? 'cn' : 'en'}
        syncInput={false}
        showSelectType={rangeTimeOtherStyle ? false : showSelectType}
        showShortcut={showShortcut}
        setShortcutOption={handleSetShortcutOption}
        startOfWeek={startOfWeek}
        autoResetShortcutByValueChange={autoResetShortcutByValueChange}
        isSingleSelect={isSingleSelect}
        autoRange={autoRange}
        type={type}
        typeList={typeList}
        value={timeVal}
        defaultValue={defaultValueProp}
        onChange={handleDateChange}
      />
    </div>
  );
};

const RangeDatePreview: React.FC<{
  item: Parameters<NonNullable<IFilterTypeEntry['renderPreview']>>[0];
  valueItem: any;
}> = (props) => {
  const { item, valueItem } = props;
  const { formatMessage } = useIntl();
  const { controlProps } = item || {};
  const { autoRange } = controlProps || {};
  const isSingleDate = autoRange?.length === 0;
  const { granularity, startDate: rawStartDate, endDate: rawEndDate } = valueItem || {};
  const dateType = getTypeByGranularity(granularity);
  const hasCompleteRange = !!rawStartDate && !!rawEndDate;
  const startDate = showTimeByDateType(rawStartDate, dateType);
  const endDate = showTimeByDateType(rawEndDate, dateType);

  let rangeText = formatMessage({ id: 'dbi_filterSave_none' });

  if (isSingleDate) {
    const date = startDate || endDate;
    if (date) {
      rangeText = date;
    }
  } else if (hasCompleteRange) {
    if (startDate && endDate) {
      rangeText = `${startDate} ~ ${endDate}`;
    }
  }

  return (
    <>
      <span className="model-form-text">
        {isReleaseRangePickerItem(item)
          ? formatMessage({ id: 'dbi_filterSave_release' })
          : formatMessage({ id: 'dbi_filterSave_duration' })}
      </span>
      <span className="model-form-context">{rangeText}</span>
    </>
  );
};

const getRenderPreview = (): NonNullable<IFilterTypeEntry['renderPreview']> => {
  // eslint-disable-next-line react/display-name
  return (item, value) => <RangeDatePreview item={item} valueItem={value} />;
};

export const rangePickerControl: IFilterControlModule = {
  type: FILTER_TYPE.RANGE_PICKER,
  entry: {
    render: (props) => {
      return <RangePickerRenderer {...props} />;
    },
  },
  getRenderPreview,
};
